/**
 * Parse a User-Agent into a short device · browser label for Account Sessions.
 * Pure — no DOM. Keep labels short for mobile rows.
 */

export function parseDeviceLabel(userAgent, intlMessages = null) {
  const ua = typeof userAgent === 'string' ? userAgent : '';
  // `{ unknownDevice: null }` = "no label" (the caller translates the fallback).
  const unknown = intlMessages && 'unknownDevice' in intlMessages ? intlMessages.unknownDevice : 'Unknown device';
  if (!ua.trim()) return unknown;

  const device = detectDevice(ua);
  const browser = detectBrowser(ua);
  if (device && browser) return `${device} · ${browser}`;
  return device || browser || unknown;
}

function detectDevice(ua) {
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Android/i.test(ua) && /Mobile/i.test(ua)) return 'Android phone';
  if (/Android/i.test(ua)) return 'Android tablet';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/CrOS/i.test(ua)) return 'Chromebook';
  if (/Linux/i.test(ua)) return 'Linux';
  return null;
}

function detectBrowser(ua) {
  // Order matters — Edge/Opera/CriOS before Chrome; Safari after Chrome checks.
  if (/Edg\//i.test(ua)) return 'Edge';
  if (/OPR\/|Opera/i.test(ua)) return 'Opera';
  if (/Firefox\//i.test(ua) || /FxiOS\//i.test(ua)) return 'Firefox';
  if (/CriOS\//i.test(ua)) return 'Chrome';
  if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) return 'Chrome';
  if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua) && !/CriOS\//i.test(ua)) return 'Safari';
  return null;
}

export function isMobileDeviceLabel(label) {
  if (!label) return false;
  return /iPhone|iPad|Android/i.test(label);
}

/**
 * Format an ALREADY-masked `ip_masked` value for display. The RPC now does
 * the masking server-side (a /24 IPv4 host or a truncated IPv6 prefix) — this
 * is a pure display formatter, not a second masking pass.
 */
export function formatMaskedIp(ipMasked) {
  if (!ipMasked || typeof ipMasked !== 'string') return null;
  // The server already coarsened the value (IPv4 /24, IPv6 /48); this only
  // shapes it for display. Strip an inet CIDR suffix and an IPv4-mapped IPv6
  // prefix ("::ffff:203.0.113.0" is a v4 client behind some proxies).
  const trimmed = ipMasked.trim().replace(/\/\d{1,3}$/, '').replace(/^::ffff:/i, '');
  if (!trimmed) return null;
  const v4 = trimmed.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.\d{1,3}$/);
  if (v4) return `${v4[1]}.${v4[2]}.${v4[3]}.x`;
  if (trimmed.includes(':')) {
    const parts = trimmed.split(':').filter(Boolean);
    if (parts.length >= 2) return `${parts[0]}:${parts[1]}:****`;
    return '****';
  }
  return trimmed;
}

export function formatSessionLocation({ city, countryCode }, fallback = null) {
  const cityClean = typeof city === 'string' ? city.trim() : '';
  const cc = typeof countryCode === 'string' ? countryCode.trim().toUpperCase() : '';
  if (cityClean && cc) return `${cityClean}, ${cc}`;
  if (cityClean) return cityClean;
  if (cc) return cc;
  return fallback;
}

/**
 * Relative last-active copy. Pass `now` for tests.
 * Returns { key, values } for intl.formatMessage, or a ready string via formatSessionLastActive.
 */
export function describeSessionActivity(isoTimestamp, { now = Date.now(), isCurrent = false } = {}) {
  if (isCurrent) {
    return { id: 'account.sessions.activeNow', defaultMessage: 'Active now' };
  }
  if (!isoTimestamp) {
    return { id: 'account.sessions.lastActiveUnknown', defaultMessage: 'Last active unknown' };
  }
  const then = new Date(isoTimestamp).getTime();
  if (Number.isNaN(then)) {
    return { id: 'account.sessions.lastActiveUnknown', defaultMessage: 'Last active unknown' };
  }
  const diffMs = Math.max(0, now - then);
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) {
    return { id: 'account.sessions.activeNow', defaultMessage: 'Active now' };
  }
  if (mins < 60) {
    return { id: 'account.sessions.minutesAgo', defaultMessage: '{n} min ago', values: { n: mins } };
  }
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return { id: 'account.sessions.hoursAgo', defaultMessage: '{n}h ago', values: { n: hours } };
  }
  const days = Math.floor(hours / 24);
  if (days === 1) {
    return { id: 'account.sessions.yesterday', defaultMessage: 'Yesterday' };
  }
  // No further tier past "days ago" — sessions are capped at 24h server-side,
  // so a value this old is already outside the realistic range and the old
  // onDate branch (which formatted with no timeZone — a tz-contract
  // violation) was unreachable in practice.
  return { id: 'account.sessions.daysAgo', defaultMessage: '{n}d ago', values: { n: days } };
}

export function getCurrentSessionIdFromAccessToken(accessToken) {
  if (!accessToken || typeof accessToken !== 'string') return null;
  try {
    const parts = accessToken.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const json = typeof atob === 'function'
      ? atob(b64 + pad)
      : Buffer.from(b64 + pad, 'base64').toString('utf8');
    const payload = JSON.parse(json);
    const sid = payload?.session_id;
    return typeof sid === 'string' && sid.length > 0 ? sid : null;
  } catch {
    return null;
  }
}
